import { computed, inject, Injector, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap, tap } from 'rxjs';

import {
  BuildLinkFromValue,
  GridColumnCasePipe,
} from '@wcg/frontend-framework';

import { GridType } from '@data-access/grid-views';

import {
  LoadGridPayload,
  STUDY_URL,
  withGridConfigFeatures,
  ORGANIZATION_STUDY_GRID_STATE,
  studyListInitialState,
  withOrganizationStudyEntities,
  CONTACT_URL,
  transformGridDateFormat,
} from '@data-access/shared';

import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';

import {
  StudyGridTypes,
  StudyListSignalState,
  OrganizationStudy,
} from '../../types';

import { StudyListService } from '../../services';

export const ORGANIZATION_STUDY_LIST_SIGNAL_STORE = signalStore(
  withState(() => inject(ORGANIZATION_STUDY_GRID_STATE)),
  withProps((store, _studyListService = inject(StudyListService)) => ({
    _gridCasePipe: new GridColumnCasePipe(),
    _studyListService,
    _shallowCamelCasePipe: new ShallowCamelCasePipe(),
    _buildLinkFromValue: new BuildLinkFromValue(),
    _wcgTableUtilService: inject(WcgTableUtilsService),
    _injector: inject(Injector),
    store,
  })),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withOrganizationStudyEntities()),
  withComputed(
    ({
      error,
      _studyEntities,
      total,
      isLoading,
      currentUrl,
      gridType,
      store,
      _injector,
    }) => ({
      $currentUrl: computed(() => currentUrl()),
      $error: computed(() => error()),

      $gridType: computed(() => {
        const _gridType: GridType = gridType();
        return _gridType;
      }),
      $isLoading: computed(() => isLoading()),
      $studies: computed(() => {
        const _studies = _studyEntities();
        if (!_studies) {
          return [] as OrganizationStudy[];
        }
        return transformGridDateFormat<OrganizationStudy>(
          _injector,
          _studies,
          store.columnsCopy(),
        );
      }),
      $totalCount: computed(() => total()),
    }),
  ),
  withMethods(
    ({
      _studyListService,
      _shallowCamelCasePipe,
      _buildLinkFromValue,
      $currentUrl,
      ...store
    }) => {
      const _loadStudyList = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) =>
            _studyListService.getStudyList($currentUrl(), params).pipe(
              map((response) => ({
                ...response,
                config: _shallowCamelCasePipe.transform(response.config),
              })),
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  patchState(store, {
                    currentStateDestroyed: false,
                    total: response.total,
                  });
                  store.loadStudies(response.data);
                  store.loadConfigurations(
                    response.config,
                    updateColumns,
                    response.data?.[0],
                    _buildLinkFromValue,
                  );
                  store.setGridViews(response.views);
                },
              }),
            ),
          ),
        ),
      );
      const _getGridMetaData = (
        type: string,
        entityId?: string,
        isPinned?: boolean,
      ): {
        currentUrl: string;
      } => {
        switch (type) {
          case 'organization':
            return {
              currentUrl: `${STUDY_URL}organization/${entityId}/`,
            };
          case 'main':
            return {
              currentUrl: isPinned ? `${STUDY_URL}?is_pinned=true` : STUDY_URL,
            };
          case 'contact':
            return {
              currentUrl: `${CONTACT_URL}/${entityId}/studies/`,
            };
          default:
            throw new Error('Invalid type for loading study grid');
        }
      };

      const _setGridMetaData = (
        type: StudyGridTypes,
        entityId?: string,
        isPinned?: boolean,
      ) => {
        const { currentUrl } = _getGridMetaData(type, entityId, isPinned);
        patchState(store, { currentUrl });
      };

      return {
        loadStudyGrid: (payload: LoadGridPayload) => {
          return _loadStudyList(payload);
        },
        resetGridSettings: (payload: LoadGridPayload) => {
          _loadStudyList(payload);
          patchState(store, { searchString: '' });
          store.setDefaultGridView();
        },
        resetState: () =>
          patchState(store, {
            ...studyListInitialState,
            currentStateDestroyed: true,
          }),
        setByURL: (
          type: StudyGridTypes,
          entityId?: string,
          isPinned?: boolean,
        ) => _setGridMetaData(type, entityId, isPinned),
      };
    },
  ),
) satisfies Type<StudyListSignalState>;
